import React, { useEffect, useState } from 'react';
import { Save, AlertCircle, CheckCircle2, Globe, Stethoscope, Users, Tv, Shield, ChevronDown, ExternalLink } from 'lucide-react';
import { apiFetch } from '../../services/cloudflareService';
import { SETTINGS_CATALOG, type SettingMeta } from '../../worker/utils/settings';

// ── This screen is SUPER ADMIN tier ──
const TIER = 'superadmin' as const;

// Filter catalog: only show settings visible to this tier
const VISIBLE_SETTINGS = SETTINGS_CATALOG.filter(s => s.tiers[TIER] !== 'hidden');

const SECTIONS = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'operations', label: 'Operaciones', icon: Stethoscope },
    { id: 'family', label: 'Experiencia Familiar', icon: Users },
    { id: 'screens', label: 'Pantallas y Publicidad', icon: Tv },
    { id: 'security', label: 'Seguridad', icon: Shield },
];

const t = {
    cardBg: 'bg-white/70 backdrop-blur-2xl',
    cardBorder: 'border-slate-200/60',
    cardShadow: 'shadow-[inset_0_1px_0_rgba(255,255,255,1),_0_8px_30px_rgba(0,0,0,0.02)]',
};

const GlobalSettings: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

    // State
    const [effectiveSettings, setEffectiveSettings] = useState<Record<string, any>>({});
    
    // Edits specifically local to THIS group (these will be sent as overrides)
    const [localOverrides, setLocalOverrides] = useState<Record<string, any>>({});
    
    // We keep track of the original overrides to know if there are unsaved changes
    const [originalOverrides, setOriginalOverrides] = useState<Record<string, any>>({});

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // Fetch global settings
                const raw = await apiFetch(`/api/settings/global`);
                if (raw.ok) {
                    const res = await raw.json();
                    if (res.success) {
                        setEffectiveSettings(res.effective);
                        setLocalOverrides(res.overrides || {});
                        setOriginalOverrides(res.overrides || {});
                    }
                }
            } catch (err) {
                console.error(err);
                setStatusMsg({ text: 'Error cargando configuraciones.', type: 'error' });
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const hasChanges = JSON.stringify(localOverrides) !== JSON.stringify(originalOverrides);

    const handleSave = async () => {
        setSaving(true);
        setStatusMsg(null);
        try {
            const rawRes = await apiFetch(`/api/settings/global`, {
                method: 'PUT',
                body: JSON.stringify({ overrides: localOverrides })
            });
            if (rawRes.ok) {
                const res = await rawRes.json();
                if (res.success) {
                    setStatusMsg({ text: 'Configuraciones globales guardadas.', type: 'success' });
                    // Re-fetch to get new origins and effective values
                    const rawRefetch = await apiFetch(`/api/settings/global`);
                    if (rawRefetch.ok) {
                        const refetch = await rawRefetch.json();
                        if (refetch.success) {
                            setEffectiveSettings(refetch.effective);
                            setLocalOverrides(refetch.overrides || {});
                            setOriginalOverrides(refetch.overrides || {});
                        }
                    }
                    setTimeout(() => setStatusMsg(null), 3000);
                } else {
                    throw new Error(res.error || 'Error desconocido');
                }
            } else {
                const text = await rawRes.text();
                throw new Error(text || 'Error desconocido');
            }
        } catch (e: any) {
            setStatusMsg({ text: e.message || 'Error guardando configuraciones', type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (key: string, value: any) => {
        setLocalOverrides(prev => ({ ...prev, [key]: value }));
    };

    if (loading) return <div className="p-8 text-slate-500">Cargando defaults globales...</div>;

    const renderInput = (meta: SettingMeta) => {
        const tierAccess = meta.tiers[TIER];
        const isReadonly = tierAccess === 'readonly';

        // Value to show: if it has an override, use that. Otherwise use Zod default (effective).
        const currentValue = effectiveSettings[meta.key];
        const isOverridden = localOverrides[meta.key] !== undefined;

        return (
            <div key={meta.key} className={`flex items-center justify-between py-4 border-b border-slate-100 last:border-0 group/row ${isReadonly ? 'opacity-60' : ''}`}>
                <div className="flex-1 pr-6">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-bold text-slate-800">{meta.label}</span>
                        {isOverridden && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-50 text-indigo-600 border border-indigo-100 tracking-wider uppercase">Default Modificado</span>
                        )}
                        {isReadonly && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-slate-100 text-slate-400 border border-slate-200 tracking-wider uppercase">Solo lectura</span>
                        )}
                    </div>
                    <p className="text-[11px] text-slate-500">{meta.description}</p>
                </div>

                <div className="flex items-center gap-3">
                    {meta.type === 'boolean' && (
                        <button
                            onClick={() => !isReadonly && handleChange(meta.key, !currentValue)}
                            disabled={isReadonly}
                            className={`w-11 h-6 rounded-full transition-colors relative ${currentValue ? 'bg-indigo-500' : 'bg-slate-200'} ${isReadonly ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                            <span className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform ${currentValue ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    )}

                    {meta.type === 'number' && meta.presets && !isReadonly ? (
                        <div className="flex items-center gap-1.5">
                            {meta.presets.map(p => (
                                <button
                                    key={p}
                                    onClick={() => handleChange(meta.key, p)}
                                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
                                        currentValue === p
                                            ? 'bg-indigo-500 text-white shadow-sm'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {p}{meta.unit ? meta.unit.charAt(0) : ''}
                                </button>
                            ))}
                            <input
                                type="number"
                                min={meta.min} max={meta.max}
                                value={currentValue || 0}
                                onChange={e => handleChange(meta.key, parseInt(e.target.value) || 0)}
                                className={`w-16 pl-2 pr-1 py-1 text-xs font-bold text-slate-600 bg-white border rounded-lg outline-none focus:border-indigo-500 ${
                                    meta.presets.includes(currentValue) ? 'border-slate-100' : 'border-indigo-300'
                                }`}
                                title="Valor personalizado"
                            />
                        </div>
                    ) : meta.type === 'number' && (
                        <div className="relative flex items-center">
                            <input
                                type="number"
                                min={meta.min} max={meta.max}
                                value={currentValue || 0}
                                onChange={e => !isReadonly && handleChange(meta.key, parseInt(e.target.value) || 0)}
                                disabled={isReadonly}
                                className={`w-20 pl-3 pr-8 py-1.5 text-sm font-bold text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-500 ${isReadonly ? 'cursor-not-allowed bg-slate-50' : ''}`}
                            />
                            {meta.unit && <span className="absolute right-3 text-xs text-slate-400 font-medium pointer-events-none">{meta.unit}</span>}
                        </div>
                    )}

                    {meta.type === 'string' && (
                        <input
                            type="text"
                            value={currentValue || ''}
                            placeholder={meta.placeholder || ''}
                            onChange={e => !isReadonly && handleChange(meta.key, e.target.value)}
                            disabled={isReadonly}
                            className={`w-48 px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-500 placeholder:text-slate-300 ${isReadonly ? 'cursor-not-allowed bg-slate-50' : ''}`}
                        />
                    )}

                    {meta.type === 'enum' && meta.enumOptions && (
                        <div className="relative">
                            <select
                                value={currentValue}
                                onChange={e => !isReadonly && handleChange(meta.key, e.target.value)}
                                disabled={isReadonly}
                                className={`appearance-none w-48 pl-3 pr-8 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg outline-none focus:border-indigo-500 cursor-pointer ${isReadonly ? 'cursor-not-allowed bg-slate-50' : ''}`}
                            >
                                {meta.enumOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="max-w-5xl mx-auto px-6 py-8">
            <div className="flex items-end justify-between mb-8">
                <div>
                    <h2 className={`text-2xl font-black text-slate-900 tracking-tight`}>Defaults Globales de Plataforma</h2>
                    <p className="text-sm text-slate-500 mt-1">Define las configuraciones base para todos los hospitales y cadenas de Hospital Status.</p>
                </div>
                
                <div className="flex flex-col items-end gap-3">
                    <div className="flex items-center gap-2">
                        {statusMsg && (
                            <span className={`text-xs font-bold flex items-center gap-1.5 ${statusMsg.type === 'success' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {statusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                                {statusMsg.text}
                            </span>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={!hasChanges || saving}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                                hasChanges
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-500/20'
                                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                            }`}
                        >
                            {saving ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                            Guardar Defaults
                        </button>
                    </div>
                </div>
            </div>
            
            <div className="mb-6 p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-start gap-3">
                <Shield className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />
                <div>
                    <h4 className="text-sm font-bold text-slate-800">Capa Base</h4>
                    <p className="text-xs text-slate-600 mt-1">Cualquier cambio aquí será el valor de fallback para hospitales y cadenas que no hayan definido su propia regla. Esto no afecta a hospitales que ya configuraron una regla manualmente.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
                {/* TOC Sidebar */}
                <div className="hidden lg:block space-y-1 sticky top-24 self-start">
                    {SECTIONS.map(sec => {
                        const count = VISIBLE_SETTINGS.filter(s => s.section === sec.id).length;
                        if (count === 0) return null;
                        return (
                            <a key={sec.id} href={`#sec-${sec.id}`} className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-bold text-slate-500 hover:text-indigo-600 hover:bg-indigo-50/50 transition-colors">
                                <sec.icon className="w-4 h-4" />
                                {sec.label}
                            </a>
                        );
                    })}
                </div>

                {/* Content */}
                <div className="space-y-6 pb-24">
                    {SECTIONS.map(sec => {
                        const sectionSettings = VISIBLE_SETTINGS.filter(s => s.section === sec.id);
                        if (sectionSettings.length === 0) return null;

                        return (
                            <div key={sec.id} id={`sec-${sec.id}`} className={`scroll-mt-24 ${t.cardBg} rounded-[1.5rem] border ${t.cardBorder} ${t.cardShadow} overflow-hidden`}>
                                <div className="px-6 py-5 border-b border-slate-100 bg-slate-50/50 flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-white shadow-sm border border-slate-200 flex items-center justify-center">
                                        <sec.icon className="w-4 h-4 text-slate-600" />
                                    </div>
                                    <h2 className="text-base font-black text-slate-800 tracking-tight">{sec.label}</h2>
                                </div>
                                <div className="px-6 py-2">
                                    {sectionSettings.map(renderInput)}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default GlobalSettings;
